import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Plus, Package, ArrowLeft, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useTranslation } from "react-i18next";

/**
 * Reusable modal to add/link a product (supply) to a supplier.
 * Step 1: search the master catalog (or create a new supply if missing).
 * Step 2: set price + min order qty + notes, then link it to the supplier.
 *
 * Props:
 *  - supplierId: id of the supplier to link the product to
 *  - existingSupplyIds: array of supply ids already linked (filtered out of search)
 *  - onClose(): close the modal
 *  - onLinked(rel): called with the created supplier-supply relation
 *  - title (optional)
 */
export default function SupplierSupplyModal({ supplierId, existingSupplyIds = [], onClose, onLinked, title }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", category: "", unit: "", presentation: "", pos_sku: "" });
  const [price, setPrice] = useState("");
  const [minQty, setMinQty] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/supplies/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setSearching(true);
    const handle = setTimeout(() => {
      api.get(`/supplies`, { params: { search: term || undefined, active_only: true } })
        .then((r) => { if (alive) setResults(r.data.filter((s) => !existingSupplyIds.includes(s.id))); })
        .catch(() => {})
        .finally(() => { if (alive) setSearching(false); });
    }, 250);
    return () => { alive = false; clearTimeout(handle); };
  }, [term]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickSupply = (s) => { setSelected(s); setStep(2); };

  const createSupply = async () => {
    if (form.name.trim().length < 2) { toast.error(t("catalog.nameRequired")); return; }
    if (!form.category.trim()) { toast.error(t("catalog.categoryRequired")); return; }
    if (!form.unit.trim()) { toast.error(t("catalog.unitRequired")); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/supplies", {
        name: form.name.trim(),
        category: form.category.trim(),
        unit: form.unit.trim(),
        presentation: form.presentation.trim() || undefined,
        pos_sku: form.pos_sku.trim() || undefined,
      });
      toast.success(t("catalog.supplyCreated"));
      setSelected(r.data);
      setCreatingNew(false);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const link = async () => {
    const p = parseFloat(price);
    if (!price || isNaN(p) || p <= 0) { toast.error(t("catalog.priceRequired")); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/suppliers/${supplierId}/supplies`, {
        supply_id: selected.id,
        price: p,
        min_order_qty: minQty ? parseFloat(minQty) : undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t("catalog.productLinked"));
      onLinked?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          data-testid="supplier-supply-modal"
          className="bg-white rounded-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[88vh]">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
                <Package className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <h2 className="font-display font-bold text-base">{title || t("catalog.addProduct")}</h2>
                <p className="text-xs text-slate-500">
                  {step === 1 ? t("catalog.step1Hint") : t("catalog.step2Hint")}
                </p>
              </div>
            </div>
            <button data-testid="ssm-close-btn" onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto">
            {step === 1 && !creatingNew && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input data-testid="ssm-search-input" autoFocus type="text" value={term}
                    onChange={(e) => setTerm(e.target.value)} placeholder={t("catalog.searchPlaceholder")}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>
                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-64 overflow-y-auto">
                  {searching && results.length === 0 ? (
                    <div className="p-4 text-center text-sm text-slate-400">{t("common.loading")}</div>
                  ) : results.length === 0 ? (
                    <div className="p-4 text-center text-sm text-slate-400">{t("catalog.noResults")}</div>
                  ) : (
                    results.map((s) => (
                      <button key={s.id} data-testid={`ssm-result-${s.id}`} onClick={() => pickSupply(s)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-[#1A1A1A]">{s.name}</div>
                          <div className="text-[11px] text-slate-500 capitalize">{s.category} · {s.unit}{s.presentation ? ` · ${s.presentation}` : ""}</div>
                        </div>
                        <Plus className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      </button>
                    ))
                  )}
                </div>
                <button data-testid="ssm-create-new-btn" onClick={() => { setCreatingNew(true); setForm((f) => ({ ...f, name: term })); }}
                  className="w-full px-4 py-2.5 border border-dashed border-slate-300 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" /> {t("catalog.createNewSupply")}
                </button>
              </>
            )}

            {step === 1 && creatingNew && (
              <>
                <Field label={t("catalog.name")}>
                  <input data-testid="ssm-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="inp" placeholder={t("catalog.namePlaceholder")} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("catalog.category")}>
                    <input data-testid="ssm-new-category" list="ssm-cats" value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })} className="inp" placeholder={t("catalog.categoryPlaceholder")} />
                    <datalist id="ssm-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                  </Field>
                  <Field label={t("catalog.unit")}>
                    <input data-testid="ssm-new-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      className="inp" placeholder={t("catalog.unitPlaceholder")} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("catalog.presentation")}>
                    <input data-testid="ssm-new-presentation" value={form.presentation} onChange={(e) => setForm({ ...form, presentation: e.target.value })}
                      className="inp" placeholder="Caja x 24" />
                  </Field>
                  <Field label={t("catalog.posSku")}>
                    <input data-testid="ssm-new-sku" value={form.pos_sku} onChange={(e) => setForm({ ...form, pos_sku: e.target.value })}
                      className="inp" placeholder="—" />
                  </Field>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="ssm-cancel-new-btn" onClick={() => setCreatingNew(false)}
                    className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4" /> {t("common.back")}
                  </button>
                  <button data-testid="ssm-create-supply-btn" onClick={createSupply} disabled={submitting}
                    className="flex-1 btn-primary-chizy px-4 py-2.5 rounded-xl text-sm flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <Plus className="w-4 h-4" /> {t("catalog.createAndContinue")}
                  </button>
                </div>
              </>
            )}

            {step === 2 && selected && (
              <>
                <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-[#1A1A1A]">{selected.name}</div>
                    <div className="text-[11px] text-slate-500 capitalize">{selected.category} · {selected.unit}</div>
                  </div>
                  <button data-testid="ssm-back-btn" onClick={() => { setStep(1); setSelected(null); }}
                    className="text-xs font-semibold text-slate-500 hover:text-[#1A1A1A] flex items-center gap-1">
                    <ArrowLeft className="w-3.5 h-3.5" /> {t("catalog.changeProduct")}
                  </button>
                </div>
                <Field label={`${t("catalog.priceMXN")} *`}>
                  <input data-testid="ssm-price-input" type="number" step="0.01" min="0" value={price}
                    onChange={(e) => setPrice(e.target.value)} autoFocus
                    className="w-full px-4 py-3 bg-white border-2 border-[#FFDF00] rounded-xl text-base font-mono font-bold focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </Field>
                <div className="grid grid-cols-1 gap-3">
                  <Field label={t("catalog.minOrderQty")}>
                    <input data-testid="ssm-minqty-input" type="number" step="0.1" min="0" value={minQty}
                      onChange={(e) => setMinQty(e.target.value)} className="inp" placeholder="—" />
                  </Field>
                  <Field label={t("catalog.notes")}>
                    <input data-testid="ssm-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="inp" placeholder={t("catalog.notesPlaceholder")} />
                  </Field>
                </div>
              </>
            )}
          </div>

          {step === 2 && (
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
                {t("common.cancel")}
              </button>
              <button data-testid="ssm-link-btn" onClick={link} disabled={submitting}
                className="btn-primary-chizy px-5 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-50">
                <Link2 className="w-4 h-4" /> {submitting ? t("common.saving") : t("catalog.linkProduct")}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{label}</label>
    {children}
  </div>
);
